import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router-dom';
import { v7 } from 'uuid';

import { ApiClientContextProvider } from '@eluve/api-client-provider';
import { ToastProvider, createWebToaster } from '@eluve/components';
import { initSuperTokensWeb } from '@eluve/feature-login';
import {
  LocalLokiLogger,
  Logger,
  LoggingContextProvider,
  errorGroupBeforeSend,
} from '@eluve/logger';
import { setupEluveMediaStreams } from '@eluve/mic-utils';
import { SentryEnvironment, initializeSentry } from '@eluve/utils';

import { installChunkReloadRecovery } from './chunk-reload-recovery';
import { appConfig } from './config';
import { reactRouter } from './router';

const isProd =
  appConfig.VITE_METRICS_ENV === 'production' ||
  appConfig.VITE_METRICS_ENV === 'production-siloam';
const datadogSite = 'us5.datadoghq.com';
const datadogClientToken = 'pub1f5d27dc59ce54610ad0852ff58ac9f2';
const datadogService = 'web-vite';
const webViteSentryDsn =
  'https://d1cb9656d1b6d1c6bfd495a2ffeb43f7@o4506339234480128.ingest.sentry.io/4506384339763200';

setupEluveMediaStreams();

datadogRum.init({
  applicationId: '47b6da0d-6022-4116-b4bb-4a526a88dfa4',
  clientToken: datadogClientToken,
  site: datadogSite,
  service: datadogService,
  env: appConfig.VITE_METRICS_ENV,
  sessionSampleRate: 100, // We can adjust this based on cost and need
  sessionReplaySampleRate: 100,
  trackUserInteractions: !isProd, // tracks user interactions such as clicks, form submissions, and other interactions with the web application.
  trackResources: true, // Track resources (ajax, css, js)
  trackLongTasks: !isProd, // enable the tracking of long tasks. Long tasks are browser tasks that take longer than 50 milliseconds to execute. These tasks can block the main thread, leading to delays in processing user inputs and rendering updates, which can negatively affect the user experience by causing noticeable UI delays and jank.
  defaultPrivacyLevel: 'mask-user-input',
  trackingConsent: 'not-granted',
});

if (appConfig.VITE_METRICS_ENV !== 'local-dev') {
  // Documentation: https://docs.datadoghq.com/logs/log_collection/javascript/
  datadogLogs.init({
    clientToken: datadogClientToken,
    site: datadogSite,
    forwardErrorsToLogs: false, // Do not forward console.error logs to Datadog
    sessionSampleRate: 100,
    service: datadogService,
    env: appConfig.VITE_METRICS_ENV,
    beforeSend: errorGroupBeforeSend,
  });

  initializeSentry({
    dsn: webViteSentryDsn,
    environment: appConfig.VITE_METRICS_ENV as SentryEnvironment,
    tenant: {
      type: 'url',
      value: window.location.pathname,
    },
    enabled: appConfig.VITE_ENV !== 'development',
    release: appConfig.VITE_APP_VERSION,
    app: 'web-vite',
  });
}

const rootLogger = new Logger('eluve-root-logger', {
  metadata: { appVersion: appConfig.VITE_APP_VERSION, tabId: v7() },
  logger: appConfig.VITE_USE_LOCAL_LOKI_LOGGER
    ? new LocalLokiLogger('web-vite', {
        worktree: appConfig.VITE_WORKTREE_BRANCH,
      })
    : undefined,
});

// For Vercel preview deployments and custom preview subdomains, use the current
// origin as websiteDomain to prevent SuperTokens from redirecting to the
// configured staging domain
const websiteDomain = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // Vercel preview URLs or custom preview subdomains
    if (hostname.endsWith('vercel.app')) {
      return window.location.origin + '/';
    }
  }
  return appConfig.VITE_SUPERTOKENS_WEBSITE_DOMAIN;
})();

initSuperTokensWeb({
  appName: 'eluve',
  websiteDomain,
  websiteBasePath: '/login',
  apiDomain: appConfig.VITE_API_DOMAIN,
  apiBasePath: '/api/auth',
});

// Armed before the router renders, since that first render is what fetches a
// route's chunk and therefore the earliest point a preload can fail.
installChunkReloadRecovery({ logger: rootLogger });

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

root.render(
  <StrictMode>
    <HelmetProvider>
      <LoggingContextProvider loggerInput={rootLogger}>
        <ToastProvider toaster={createWebToaster()}>
          <ApiClientContextProvider baseUrl={appConfig.VITE_API_DOMAIN}>
            <NuqsAdapter>
              <RouterProvider
                router={reactRouter}
                future={{ v7_startTransition: true }}
              />
            </NuqsAdapter>
          </ApiClientContextProvider>
        </ToastProvider>
      </LoggingContextProvider>
    </HelmetProvider>
  </StrictMode>,
);

// Getting this far means the bundle parsed and booted — stop the pre-boot
// browser check (public/browser-check.js) from beaconing later script errors.
(
  window as unknown as { __eluveBrowserCheck?: { disarm: () => void } }
).__eluveBrowserCheck?.disarm();
